import { useState, useEffect } from 'react';
import { X, Cookie, Settings, Shield } from 'lucide-react';

const CONSENT_KEY = 'fesch_consent_v1';

const DEFAULT_PREFS = { essential: true, analytics: false, marketing: false };

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeConsent(prefs) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ timestamp: Date.now(), prefs })
    );
  } catch (e) {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent('fesch:consent-updated', { detail: prefs }));
}

// Public helpers (can be imported anywhere)
export function hasAnalyticsConsent() {
  const c = readConsent();
  return !!(c && c.prefs && c.prefs.analytics);
}

export function hasMarketingConsent() {
  const c = readConsent();
  return !!(c && c.prefs && c.prefs.marketing);
}

export function getConsent() {
  return readConsent();
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent('fesch:consent-open'));
}

function Toggle({ label, description, checked, onChange, disabled }) {
  return (
    <label className="flex items-start gap-4 cursor-pointer">
      <div className="relative mt-1">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          className="sr-only peer"
          data-testid={`cookie-toggle-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
        <div
          className={
            'w-11 h-6 rounded-full transition-colors ' +
            (disabled ? 'bg-zinc-300' : checked ? 'bg-[#E31E24]' : 'bg-zinc-200')
          }
        />
        <div
          className={
            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ' +
            (checked ? 'translate-x-5' : '')
          }
        />
      </div>
      <div className="flex-1">
        <div className="font-semibold text-neutral-900 text-sm">{label}</div>
        <div className="text-xs text-neutral-500 mt-0.5">{description}</div>
      </div>
    </label>
  );
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else if (existing.prefs) {
      setPrefs({ ...DEFAULT_PREFS, ...existing.prefs });
    }

    const onOpen = () => {
      const current = readConsent();
      if (current && current.prefs) {
        setPrefs({ ...DEFAULT_PREFS, ...current.prefs });
      }
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener('fesch:consent-open', onOpen);
    return () => window.removeEventListener('fesch:consent-open', onOpen);
  }, []);

  function save(next) {
    writeConsent(next);
    setPrefs(next);
    setVisible(false);
    setShowSettings(false);
  }

  function acceptAll() {
    save({ essential: true, analytics: true, marketing: true });
  }

  function rejectAll() {
    save({ essential: true, analytics: false, marketing: false });
  }

  function saveSelection() {
    save(prefs);
  }

  function update(key, value) {
    setPrefs((p) => ({ ...p, [key]: value }));
  }

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end justify-center p-4 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="pointer-events-auto w-full max-w-2xl bg-white shadow-2xl border border-zinc-200">
        {showSettings ? (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Settings className="w-6 h-6 text-[#E31E24]" />
                <h3 className="text-lg font-bold text-neutral-900">Cookie-Einstellungen</h3>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="text-neutral-400 hover:text-neutral-600"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-5 mb-6">
              <Toggle
                label="Essenziell"
                description="Technisch notwendig, damit die Website funktioniert und Ihre Einwilligung gespeichert werden kann. Nicht deaktivierbar."
                checked={true}
                onChange={() => {}}
                disabled
              />
              <Toggle
                label="Analyse & Statistik"
                description="Helfen uns, die Nutzung der Website anonymisiert zu verstehen. Werden nur nach Ihrer Einwilligung geladen. (Aktuell nicht im Einsatz, Einstellung wird für künftige Dienste vorgemerkt.)"
                checked={prefs.analytics}
                onChange={(e) => update('analytics', e.target.checked)}
              />
              <Toggle
                label="Marketing"
                description="Für personalisierte Inhalte oder Re-Marketing-Maßnahmen. (Aktuell nicht im Einsatz.)"
                checked={prefs.marketing}
                onChange={(e) => update('marketing', e.target.checked)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={saveSelection}
                data-testid="cookie-save-selection"
                className="flex-1 bg-[#E31E24] text-white font-semibold py-3 px-6 hover:bg-[#c91a1f] transition-colors text-sm"
              >
                Auswahl speichern
              </button>
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-all-settings"
                className="flex-1 bg-neutral-900 text-white font-semibold py-3 px-6 hover:bg-neutral-800 transition-colors text-sm"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            <div className="flex items-start gap-4 mb-5">
              <div className="w-12 h-12 bg-red-50 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-[#E31E24]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-neutral-900 mb-2">
                  Wir respektieren Ihre Privatsphäre
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  Diese Website setzt aktuell keine Tracking- oder Marketing-Cookies.
                  Mit Ihrer Einwilligung dürfen wir künftig anonymisierte Statistik- oder
                  Marketing-Dienste nachladen. Sie können Ihre Auswahl jederzeit
                  über die „Cookie-Einstellungen" im Footer ändern.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="flex-1 bg-[#E31E24] text-white font-semibold py-3 px-6 hover:bg-[#c91a1f] transition-colors text-sm"
              >
                Alle akzeptieren
              </button>
              <button
                onClick={rejectAll}
                data-testid="cookie-reject-all"
                className="flex-1 bg-neutral-100 text-neutral-900 font-semibold py-3 px-6 hover:bg-neutral-200 transition-colors text-sm border border-zinc-200"
              >
                Nur essenzielle
              </button>
              <button
                onClick={() => setShowSettings(true)}
                data-testid="cookie-settings"
                className="flex items-center justify-center gap-2 px-6 py-3 text-neutral-600 hover:text-neutral-900 transition-colors text-sm font-medium"
              >
                <Settings className="w-4 h-4" />
                Einstellungen
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-100 flex items-center justify-center gap-4 text-xs text-neutral-500">
              <a
                href="/datenschutzerklarung"
                className="hover:text-[#E31E24] transition-colors flex items-center gap-1"
              >
                <Shield className="w-3 h-3" />
                Datenschutzerklärung
              </a>
              <span>·</span>
              <a href="/impressum" className="hover:text-[#E31E24] transition-colors">
                Impressum
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookieConsent;
